import { Regions } from '@smh/projects/regions';
import { componentFactoryOf } from 'vue-tsx-support';

import {
  JtnUiBtnIcon,
  JtnUiTextarea,
  JtnUiBtn,
  JtnUiRadioButton,
  JtnUiSuccessMessage,
  JtnUiTypography,
  JtnUiSpin
} from '@jtnews/jtn-ui';
import { injectStylesMixin, tsStoreMixin } from '@jtnews/shared';
import { HTML_TAGS_REGEX } from '@jtnews/shared/data';

import styles from './feedback-to-error-form.styles.scss?module';

type ErrorType = 'typo' | 'fact';
type RecordData = { data: { id: number; header: string } };
type FormEvent = { target: HTMLInputElement };
type CatchError = { data?: { status: string; detail: string } } | undefined;

type ComponentData = {
  isFormSubmitted: boolean;
  isSendSuccessfully: boolean;
  maxMessageLength: number;
  selectedText: string;
  prevText: string;
  nextText: string;
  message: string;
  errorType: ErrorType;
  captchaToken: string;
  isCaptchaError: boolean;
  errorModalMsg: string;
  apiErrorMsg: string;
  isFetching: boolean;
};

type ModalEvents = {
  onClosed(): void;
};

const MAX_SIZE_SELECTED_TEXT = 255;
const CLOSE_ICON_SIZE = 18;

export default componentFactoryOf<ModalEvents>()
  .mixin(injectStylesMixin(styles))
  .mixin(tsStoreMixin)
  .create({
    name: 'FeedbackToErrorForm',
    data(): ComponentData {
      return {
        isFormSubmitted: false,
        isSendSuccessfully: false,
        maxMessageLength: 500,
        selectedText: '',
        prevText: '',
        nextText: '',
        message: '',
        errorType: 'typo',
        captchaToken: '',
        isCaptchaError: false,
        errorModalMsg: '',
        apiErrorMsg: '',
        isFetching: false
      };
    },
    computed: {
      regionId(): Regions {
        return this.store.regionId;
      },
      isMobile(): boolean {
        return this.store.deviceInfo.isMobile && !this.store.deviceInfo.isTablet;
      },
      captchaText(): string {
        return this.store.commonModule.captchaText;
      },
      recordData(): RecordData['data'] {
        return (this.store.layoutModule.record as RecordData).data;
      },
      recordId(): number {
        return this.recordData.id;
      },
      recordHeader(): string {
        return this.recordData.header;
      },
      messageError(): string {
        if (!this.isFormSubmitted) {
          return '';
        }
        if (this.message.trim().length === 0 && this.errorType === 'fact') {
          return 'Введите текст сообщения';
        }
        if (this.message.trim().length < 3 && this.errorType === 'fact') {
          return 'Текст сообщения должен быть не меньше трех символов';
        }
        if (HTML_TAGS_REGEX.exec(this.message) !== null) {
          return 'Текст сообщения содержит недопустимые символы';
        }
        if (this.message.length > 500) {
          return 'Максимально допустимое количество символов - 500';
        }

        return '';
      },
      leftSlotSubmitBtnContent() {
        return this.isFetching ? (
          <JtnUiSpin slot="left" size="small" />
        ) : (
          <span slot="left"></span>
        );
      }
    },
    watch: {
      message(current: string) {
        if (current.length > this.maxMessageLength) {
          this.message = current.slice(0, this.maxMessageLength);
        }
      }
    },
    mounted() {
      this.beforeOpen();
    },
    methods: {
      beforeOpen() {
        const selection = window.getSelection();
        this.selectedText = selection?.toString() || '';

        if (selection === null || this.selectedText.length < 3) {
          this.errorModalMsg = 'Выделено слишком мало текста';
          return;
        }

        if (this.selectedText.length > MAX_SIZE_SELECTED_TEXT) {
          this.errorModalMsg = 'Выделено слишком много текста';
          return;
        }

        this.prevText = this.getPrevSelectionText(selection);
        this.nextText = this.getNextSelectionText(selection);
      },
      getPrevSelectionText(selectionText: Selection): string {
        const range: Range = selectionText.getRangeAt(0);
        const prevRange = range.cloneRange();
        prevRange.setStartBefore(document.body);
        prevRange.setEnd(range.startContainer, range.startOffset);
        const prevText = this.filterText(prevRange.toString());
        return prevText.substr(prevText.length - 30);
      },
      getNextSelectionText(selectionText: Selection): string {
        const range = selectionText.getRangeAt(0);
        const nextRange = range.cloneRange();
        nextRange.setStart(range.endContainer, range.endOffset);
        nextRange.setEndAfter(document.body);
        return this.filterText(nextRange.toString()).substr(0, 30);
      },
      filterText(text: string): string {
        return text
          .replace(/[\r\n]+/g, ' ')
          .replace(/\s{2,}/g, ' ')
          .replace(/<[^>]*>/g, '')
          .replace(/^\s+|\s+$/g, '');
      },
      changeErrorType(event: FormEvent) {
        this.errorType = event.target.value as ErrorType;
      },
      generatingData(): FormData {
        const formData = new FormData();

        formData.append('type', this.errorType);
        formData.append('text', this.selectedText);
        formData.append('prev', this.prevText);
        formData.append('next', this.nextText);
        formData.append('description', this.message);
        formData.append('url', window.location.href);
        formData.append('title', this.recordHeader);
        formData.append('entity', this.recordId.toString());
        formData.append('region', this.regionId.toString());
        formData.append('captchaToken', this.captchaToken);

        return formData;
      },
      async formSubmit(event: Event) {
        event.preventDefault();
        this.isFormSubmitted = true;
        if (this.messageError.length > 0) {
          return;
        }

        this.isFetching = true;
        this.captchaToken = (await this.executeCaptchaToken()) || '';
        const formData = this.generatingData();
        void this.sendMistakes(formData);
      },
      async sendMistakes(formData: FormData) {
        this.apiErrorMsg = '';

        try {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
          await this.store.newsApiClient.sendMistakesReport(this.regionId, formData);

          this.isSendSuccessfully = true;
          this.isCaptchaError = false;
        } catch (err) {
          console.error(err);
          const error = (err as CatchError)?.data || undefined;
          if (typeof error !== 'undefined') {
            if (Number(error.status) === 403) {
              this.isCaptchaError = true;
            }
            this.apiErrorMsg = error.detail;
          } else {
            this.apiErrorMsg = 'Не удалось отправить сообщение. Попробуйте еще раз.';
          }

          this.isSendSuccessfully = false;
        } finally {
          this.isFetching = false;
        }
      },
      async executeCaptchaToken(): Promise<string | undefined> {
        try {
          return await this.store.commonModule.executeCaptchaToken('send_mistakes');
        } catch (error) {
          console.error(error);
        }
      },
      closeModal() {
        this.clearForm();
        this.$emit('closed');
      },
      clearForm() {
        this.selectedText = '';
        this.message = '';
        this.apiErrorMsg = '';
        this.isFormSubmitted = false;
        this.captchaToken = '';
        this.isCaptchaError = false;
        this.isSendSuccessfully = false;
        this.prevText = '';
        this.nextText = '';
        this.errorType = 'typo';
        this.errorModalMsg = '';
      }
    },
    render() {
      return (
        <section class={styles.feedbackToErrorModal}>
          {this.errorModalMsg.length === 0 && (
            <JtnUiBtnIcon
              class={styles.closeBtn}
              btn-type="button-other-type-3"
              size={CLOSE_ICON_SIZE}
              onClick={() => this.closeModal()}
            >
              <svg width={CLOSE_ICON_SIZE} height={CLOSE_ICON_SIZE}>
                <use xlinkHref="/dist/legacy/svg-sprites/jtn-critical.42b7545660e4f467e75d4b37a02533e6.svg#jtn-critical-close"></use>
              </svg>
            </JtnUiBtnIcon>
          )}
          {this.errorModalMsg.length > 0 ? (
            <div>
              <JtnUiTypography class={styles.errorModal} tag="p">
                {this.errorModalMsg}
              </JtnUiTypography>
              <JtnUiBtn class={styles.cancelBtn} onClick={() => this.closeModal()}>
                Отменить
              </JtnUiBtn>
            </div>
          ) : (
            <div>
              <JtnUiTypography class={styles.title} tag="h3" type="font-format-news">
                Связь с редакцией
              </JtnUiTypography>
              {this.isSendSuccessfully ? (
                <JtnUiSuccessMessage />
              ) : (
                <form
                  class={styles.form}
                  onSubmit={(event: Event) => void this.formSubmit(event)}
                >
                  <p class={[styles.label, styles.textError]}>Ошибка в тексте</p>
                  <p class={styles.selectedText}>{this.selectedText}</p>

                  <div class={styles.errorsTypeGroup}>
                    <p class={[styles.label, styles.errorType]}>Тип ошибки</p>
                    <JtnUiRadioButton
                      class={styles.errorsTypeBtn}
                      name="error-type"
                      value="typo"
                      checked={this.errorType === 'typo'}
                      onChange={(event: FormEvent) => this.changeErrorType(event)}
                    >
                      Грамматическая
                    </JtnUiRadioButton>
                    <JtnUiRadioButton
                      class={styles.errorsTypeBtn}
                      name="error-type"
                      value="fact"
                      checked={this.errorType === 'fact'}
                      onChange={(event: FormEvent) => this.changeErrorType(event)}
                    >
                      Фактологическая
                    </JtnUiRadioButton>
                  </div>
                  <JtnUiTextarea
                    id="feedback_error_message"
                    class={styles.formTextarea}
                    label="Текст сообщения"
                    maxLength={this.maxMessageLength}
                    value={this.message}
                    required={this.errorType === 'fact'}
                    hasError={this.messageError.length > 0}
                    onInput={(event: FormEvent) => (this.message = event.target.value)}
                  >
                    {this.messageError.length > 0 && (
                      <span slot="error">{this.messageError}</span>
                    )}
                  </JtnUiTextarea>
                  {!this.isCaptchaError && (
                    <div class={styles.buttonsGroup}>
                      <JtnUiBtn class={styles.btn} type="submit">
                        Отправить
                        {this.leftSlotSubmitBtnContent}
                      </JtnUiBtn>
                      <JtnUiTypography
                        class={styles.recaptchaSubmitNote}
                        tag="p"
                        type="font-vijet-type-1"
                        linkType="link-blue"
                      >
                        <div domPropsInnerHTML={this.captchaText} />
                      </JtnUiTypography>
                    </div>
                  )}

                  {this.apiErrorMsg.length > 0 && (
                    <JtnUiTypography
                      class={styles.error}
                      tag="p"
                      link-type="link-red-bold"
                    >
                      <span domPropsInnerHTML={this.apiErrorMsg} />
                    </JtnUiTypography>
                  )}
                </form>
              )}
            </div>
          )}
        </section>
      );
    }
  });
